/**
 * Observability adapter.
 *
 * Provides structured logging with environment-aware behavior.
 */

import { config } from "./config"

export type LogLevel = "debug" | "info" | "warn" | "error"

export interface LogEntry {
  level: LogLevel
  message: string
  timestamp: string
  context?: Record<string, unknown>
}

/** Observability adapter for frontend logging */
export const obs = {
  /**
   * Log a debug message (only in development)
   */
  debug(message: string, context?: Record<string, unknown>): void {
    if (config.debug) {
      console.debug(`[DEBUG] ${message}`, context ?? "")
    }
  },

  /**
   * Log an info message
   */
  info(message: string, context?: Record<string, unknown>): void {
    console.info(`[INFO] ${message}`, context ?? "")
  },

  /**
   * Log a warning message
   */
  warn(message: string, context?: Record<string, unknown>): void {
    console.warn(`[WARN] ${message}`, context ?? "")
  },

  /**
   * Log an error message
   */
  error(message: string, error?: unknown, context?: Record<string, unknown>): void {
    console.error(`[ERROR] ${message}`, error ?? "", context ?? "")
  },

  /**
   * Create a child logger with fixed context
   */
  child(baseContext: Record<string, unknown>) {
    return {
      debug: (msg: string, ctx?: Record<string, unknown>) =>
        obs.debug(msg, { ...baseContext, ...ctx }),
      info: (msg: string, ctx?: Record<string, unknown>) =>
        obs.info(msg, { ...baseContext, ...ctx }),
      warn: (msg: string, ctx?: Record<string, unknown>) =>
        obs.warn(msg, { ...baseContext, ...ctx }),
      error: (msg: string, err?: unknown, ctx?: Record<string, unknown>) =>
        obs.error(msg, err, { ...baseContext, ...ctx }),
    }
  },
}
